import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
const LayoutContext = createContext(null);
const useLayoutContext = () => {
    const context = useContext(LayoutContext);
    if (!context) {
        throw new Error('LayoutItem must be used within LayoutContainer');
    }
    return context;
};
export const LayoutContainer = ({ children, className }) => {
    const containerRef = useRef(null);
    const [layoutItems, setLayoutItems] = useState([]);
    const resizeObserverRef = useRef(null);
    const recalculateLayout = useCallback(() => {
        if (!containerRef.current)
            return;
        const containerHeight = containerRef.current.getBoundingClientRect().height;
        const autoItems = layoutItems.filter(item => item.type === 'auto');
        const stretchItems = layoutItems.filter(item => item.type === 'stretch');
        const autoItemsHeight = autoItems.reduce((total, item) => {
            const height = item.element.getBoundingClientRect().height;
            return total + height;
        }, 0);
        const remainingHeight = containerHeight - autoItemsHeight;
        const heightPerStretchItem = stretchItems.length > 0 ? remainingHeight / stretchItems.length : 0;
        stretchItems.forEach(item => {
            item.element.style.height = `${heightPerStretchItem}px`;
        });
    }, [layoutItems]);
    const registerLayoutItem = useCallback((item) => {
        setLayoutItems(prevItems => {
            const existingItemIndex = prevItems.findIndex(prevItem => prevItem.id === item.id);
            if (existingItemIndex !== -1) {
                const updatedItems = [...prevItems];
                updatedItems[existingItemIndex] = item;
                return updatedItems;
            }
            return [...prevItems, item];
        });
    }, []);
    const unregisterLayoutItem = useCallback((itemId) => {
        setLayoutItems(prevItems => prevItems.filter(item => item.id !== itemId));
    }, []);
    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }
        resizeObserverRef.current = new ResizeObserver(() => {
            recalculateLayout();
        });
        resizeObserverRef.current.observe(containerRef.current);
        layoutItems.forEach(item => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.observe(item.element);
            }
        });
        recalculateLayout();
        return () => {
            resizeObserverRef.current?.disconnect();
        };
    }, [layoutItems, recalculateLayout]);
    const contextValue = {
        registerLayoutItem,
        unregisterLayoutItem,
        containerElement: containerRef.current
    };
    return (_jsx(LayoutContext.Provider, { value: contextValue, children: _jsx("div", { ref: containerRef, className: className, children: children }) }));
};
export const LayoutItem = ({ children, type = 'auto', className }) => {
    const itemRef = useRef(null);
    const { registerLayoutItem, unregisterLayoutItem } = useLayoutContext();
    const itemIdRef = useRef(`layout-item-${Math.random().toString(36).substr(2, 9)}`);
    useEffect(() => {
        if (!itemRef.current) {
            return undefined;
        }
        const itemData = {
            id: itemIdRef.current,
            element: itemRef.current,
            type
        };
        registerLayoutItem(itemData);
        return () => {
            unregisterLayoutItem(itemIdRef.current);
        };
    }, [type, registerLayoutItem, unregisterLayoutItem]);
    return (_jsx("div", { ref: itemRef, className: className, style: {
            flexShrink: type === 'stretch' ? 1 : 0,
            flexGrow: type === 'stretch' ? 1 : 0
        }, children: children }));
};
//# sourceMappingURL=layout.js.map